import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '@/components/UI/card';
import { Button } from '@/components/UI/button';
import { Download, Loader2, FileText, AlertCircle } from 'lucide-react';
import { useToast } from '@/components/UI/use-toast';
import { Alert, AlertDescription } from '@/components/UI/alert';
import { loamsApi } from '@/services/api';
import certificateService from '@/services/certificateService';
// Import the logos
import phoenicianLogoSrc from '@/assets/PhoenicianLogo.png';
import stampImageSrc from '@/assets/Sign.png'; // Phoenician stamp image
import certificationLogosSrc from '@/assets/ZZZZ.png'; // QS, ELEC, etc logos

/**
 * CompletionCertificate Component - NEW TEMPLATE FORMAT
 * =====================================================
 * Matches the official Phoenician Technical Services LLC template
 * 
 * Layout:
 * - Header: Company info (left) | Logo (right)
 * - Title: "COMPLETION CERTIFICATE" in gold
 * - To: Payee name, PO Box 2195, TRN
 * - Service Details table
 * - Certification section with signature box
 * - Footer: Address (left) | Certification logos (right)
 */

const CompletionCertificate = ({ 
  cluster, 
  agreementRef, 
  month, 
  serviceType: propServiceType,
  payee: propPayee,
  previewOnly = false,
  autoGenerate = false,
  onGenerated = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const [certificateData, setCertificateData] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dbServiceType, setDbServiceType] = useState('');
  const [dbPayee, setDbPayee] = useState('');
  const [fetchingServiceType, setFetchingServiceType] = useState(false);
  
  // Refs for generation control
  const hasStartedGenerationRef = useRef(false);
  const isGeneratingRef = useRef(false);
  const onGeneratedRef = useRef(onGenerated);
  const certificateRef = useRef(null);
  
  const { toast } = useToast();
  
  // Keep onGenerated ref updated
  useEffect(() => {
    onGeneratedRef.current = onGenerated;
  }, [onGenerated]);
  
  // Format month string to display format (e.g., "2025-04" -> "April 2023")
  const formatMonthDisplay = useCallback((monthStr) => {
    if (!monthStr) return '';
    
    try {
      const [year, monthNum] = monthStr.split('-');
      const date = new Date(parseInt(year), parseInt(monthNum) - 1, 1);
      return `${date.toLocaleString('default', { month: 'long' })} ${year}`;
    } catch {
      return monthStr;
    }
  }, []);

  // Get service type display text
  const getServiceTypeDisplay = useCallback((serviceType) => {
    if (!serviceType) return 'General Maintenance';
    
    const type = serviceType.toUpperCase();
    if (type.includes('SWIMMING') || type.includes('POOL')) {
      return 'Swimming Pool Cleaning & Maintenance';
    }
    if (type.includes('LANDSCAPE') || type.includes('IRRIGATION')) {
      return 'Landscaping Maintenance';
    }
    return serviceType;
  }, []);

  // Get details description
  const getDetailsDescription = useCallback((clusterName, serviceType, monthStr) => {
    const type = (serviceType || '').toUpperCase();
    const monthDisplay = formatMonthDisplay(monthStr);
    
    if (type.includes('SWIMMING') || type.includes('POOL')) {
      return `${clusterName} - Swimming Pool Cleaning & Maintenance for the month of ${monthDisplay}.`;
    }
    if (type.includes('LANDSCAPE') || type.includes('IRRIGATION')) {
      return `${clusterName} - Landscaping Maintenance for the month of ${monthDisplay}.`;
    }
    return `${clusterName} - General Maintenance for the month of ${monthDisplay}.`;
  }, [formatMonthDisplay]);

  // Get certificate render data
  const getCertificateRenderData = useCallback(() => {
    const finalServiceType = dbServiceType || propServiceType || '';
    const finalPayee = dbPayee || propPayee || 'LOAMS LLC';
    
    const isSwimming = finalServiceType?.toUpperCase()?.includes('SWIMMING') || 
                       finalServiceType?.toUpperCase()?.includes('POOL');
    
    return {
      cluster,
      agreementRef,
      month,
      monthDisplay: formatMonthDisplay(month),
      serviceType: finalServiceType,
      serviceTypeDisplay: getServiceTypeDisplay(finalServiceType),
      payee: finalPayee,
      isSwimming,
      details: getDetailsDescription(cluster, finalServiceType, month),
      // Hardcoded values as per requirements
      trn: '100034783900003',
      poBox: 'PO Box 2195, Dubai, UAE',
      companyTrn: '100034783900003',
      companyPhone: '+97148829484',
      companyAddress: 'Schon Business Park, Office 399, Dubai Investment Park',
    };
  }, [cluster, agreementRef, month, dbServiceType, propServiceType, dbPayee, propPayee, formatMonthDisplay, getServiceTypeDisplay, getDetailsDescription]);
  
  // Fetch agreement data from database
  useEffect(() => {
    if (cluster && agreementRef) {
      const fetchAgreementData = async () => {
        if (!cluster || !agreementRef || cluster.trim() === '' || agreementRef.trim() === '') {
          console.error('[CERT] ❌ Invalid parameters:', { cluster, agreementRef });
          setDbServiceType('');
          setDbPayee('LOAMS LLC');
          setFetchingServiceType(false);
          return;
        }
        
        setFetchingServiceType(true);
        try {
          console.log('[CERT] 🔍 Fetching agreement data:', { cluster, agreementRef });
          
          const response = await loamsApi.getAgreementDetails(cluster, agreementRef);
          
          if (response.success && response.data) {
            console.log('[CERT] ✅ Retrieved agreement data:', {
              serviceType: response.data.formatted_service_type || response.data.service_type,
              payee: response.data.payee
            });
            
            setDbServiceType(response.data.formatted_service_type || response.data.service_type || '');
            const payeeValue = response.data.payee || propPayee || 'LOAMS LLC';
            setDbPayee(payeeValue);
            
          } else {
            console.warn('[CERT] ⚠️ No agreement data found, using provided values');
            setDbServiceType(propServiceType || '');
            setDbPayee(propPayee || 'LOAMS LLC');
          }
        } catch (error) {
          console.error('[CERT] ❌ Error fetching agreement data:', error);
          setDbServiceType(propServiceType || '');
          setDbPayee(propPayee || 'LOAMS LLC');
        } finally {
          setFetchingServiceType(false);
        }
      };
      
      fetchAgreementData();
    } else {
      setDbServiceType(propServiceType || '');
      setDbPayee(propPayee || 'LOAMS LLC');
    }
  }, [cluster, agreementRef, propServiceType, propPayee]);

  // Generate PDF
  const generatePDF = useCallback(async () => {
    if (isGeneratingRef.current) {
      console.log('[CERT] ⚠️ Already generating, skipping duplicate call');
      return;
    }
    
    if (!certificateRef.current) {
      console.error('[CERT] ❌ Certificate ref is null');
      onGeneratedRef.current({ success: false, error: 'Certificate element not found' });
      return;
    }
  
    isGeneratingRef.current = true;
    console.log('[CERT] 🚀 Starting PDF generation for:', agreementRef);
    setLoading(true);
  
    try {
      console.log('[CERT] 📦 Loading html2canvas...');
      const html2canvas = (await import('html2canvas')).default;
      
      console.log('[CERT] 📦 Loading jsPDF...');
      const { jsPDF } = await import('jspdf');
      
      // Wait for images to load
      console.log('[CERT] ⏳ Waiting for images (800ms)...');
      await new Promise(resolve => setTimeout(resolve, 800));
      
      if (!certificateRef.current) {
        throw new Error('Certificate element was removed during generation');
      }
      
      console.log('[CERT] 📸 Capturing canvas...');
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#FFFFFF',
        width: certificateRef.current.scrollWidth,
        height: certificateRef.current.scrollHeight,
      });
      
      console.log('[CERT] ✅ Canvas captured:', canvas.width, 'x', canvas.height);
      
      console.log('[CERT] 📄 Creating PDF...');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.95), 
        'JPEG', 
        0, 
        0, 
        imgWidth, 
        Math.min(imgHeight, pageHeight), 
        '', 
        'FAST'
      );
      
      while (pdf.getNumberOfPages() > 1) {
        pdf.deletePage(pdf.getNumberOfPages());
      }
      
      console.log('[CERT] 💾 Generating PDF blob...');
      const pdfBlob = pdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      
      const certData = getCertificateRenderData();
      const monthParts = certData.monthDisplay.split(' ');
      const refPrefix = certData.isSwimming ? "CC" : "PO";
      const filename = `${refPrefix}_${agreementRef}_${monthParts[0]}_${monthParts[1]}.pdf`;
      
      console.log('[CERT] 📁 Filename:', filename);
      
      setCertificateData(prev => ({
        ...certData,
        url,
        filename
      }));
      
      // Record generation
      try {
        await certificateService.recordGeneration({
          cluster,
          agreementRef,
          month,
          serviceType: certData.serviceType
        });
      } catch (recordError) {
        console.warn('[CERT] ⚠️ Failed to record generation:', recordError);
      }
      
      const response = {
        success: true,
        url,
        filename,
        certificate: certData,
        blob: pdfBlob
      };
      
      console.log('[CERT] ✅✅✅ Certificate generated:', filename);
      onGeneratedRef.current(response);
      
      if (!autoGenerate) {
        toast({
          title: "Success",
          description: "Certificate generated successfully"
        });
      }
      
    } catch (err) {
      console.error("[CERT] ❌ PDF generation error:", err);
      
      const errorMessage = err.message || "Failed to generate PDF";
      setError(errorMessage);
      
      onGeneratedRef.current({ 
        success: false, 
        error: errorMessage,
        details: err.toString()
      });
      
      if (!autoGenerate) {
        toast({
          variant: "destructive",
          title: "PDF Generation Failed",
          description: errorMessage
        });
      }
      
    } finally {
      setLoading(false);
      isGeneratingRef.current = false;
    }
  }, [cluster, agreementRef, month, autoGenerate, toast, getCertificateRenderData]);

  // Auto-generate effect
  useEffect(() => {
    if (!autoGenerate) return;
    if (fetchingServiceType) return;
    if (hasStartedGenerationRef.current) return;
    if (!dbServiceType) return;
    if (!dbPayee) return;
    if (!certificateRef.current) return;
    
    console.log('[CERT] ✅ All conditions met, starting generation...');
    hasStartedGenerationRef.current = true;
    
    const timerId = setTimeout(() => {
      generatePDF();
    }, 1200);
    
    return () => {
      if (!isGeneratingRef.current && !hasStartedGenerationRef.current) {
        clearTimeout(timerId);
      }
    };
  }, [autoGenerate, fetchingServiceType, dbServiceType, dbPayee, generatePDF]);

  const setRef = useCallback((node) => {
    certificateRef.current = node;
  }, []);
  
  const generateCertificate = () => {
    if (!cluster || !agreementRef || !month) {
      toast({
        variant: "destructive",
        title: "Missing Information",
        description: "Please fill in all required fields"
      });
      return;
    }
    generatePDF();
  };
  
  const downloadCertificate = async () => {
    if (!certificateData?.url) {
      generateCertificate();
      return;
    }
    
    setDownloadLoading(true);
    try {
      const link = document.createElement('a');
      link.href = certificateData.url;
      link.download = certificateData.filename || 'certificate.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Success",
        description: "Certificate downloaded successfully"
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Download Failed",
        description: error.message || "Failed to download certificate"
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  const renderData = getCertificateRenderData();
  
  // AUTO-GENERATE MODE
  if (autoGenerate) {
    return (
      <div className="w-full">
        <div 
          ref={setRef}
          className="certificate-content bg-white" 
          style={{ 
            width: '210mm',
            minHeight: '297mm',
            position: 'relative',
            padding: '15mm 20mm',
            fontFamily: 'Arial, sans-serif',
            boxSizing: 'border-box',
            margin: '0 auto',
            background: '#FFFFFF'
          }}
        >
          <CertificateTemplate 
            data={renderData} 
            phoenicianLogoSrc={phoenicianLogoSrc}
            stampImageSrc={stampImageSrc}
            certificationLogosSrc={certificationLogosSrc}
          />
        </div>
      </div>
    );
  }
  
  // MANUAL MODE
  return (
    <div className="w-full max-w-3xl mx-auto">
      {!previewOnly && (
        <div className="mb-4">
          <Button 
            onClick={generateCertificate} 
            disabled={loading || !cluster || !agreementRef || !month || fetchingServiceType}
            className="w-full"
            style={{ backgroundColor: '#A2732E' }}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...
              </>
            ) : fetchingServiceType ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Fetching service type...
              </>
            ) : (
              <>
                <FileText className="mr-2 h-4 w-4" /> Generate Certificate
              </>
            )}
          </Button>
        </div>
      )}
      
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {(certificateData || previewOnly) && (
        <Card className="bg-white p-4 print:shadow-none">
          <CardContent className="p-0">
            <div className="mb-4 flex justify-end print:hidden">
              <Button 
                variant="outline"
                onClick={downloadCertificate}
                disabled={downloadLoading}
              >
                {downloadLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Download
              </Button>
            </div>
            
            <div 
              ref={certificateRef} 
              className="certificate-content bg-white" 
              style={{ 
                width: '210mm',
                minHeight: '297mm',
                position: 'relative',
                padding: '15mm 20mm',
                fontFamily: 'Arial, sans-serif',
                boxSizing: 'border-box',
                margin: '0 auto'
              }}
            >
              <CertificateTemplate 
                data={renderData} 
                phoenicianLogoSrc={phoenicianLogoSrc}
                stampImageSrc={stampImageSrc}
                certificationLogosSrc={certificationLogosSrc}
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/**
 * CertificateTemplate Component
 * =============================
 * The actual certificate layout matching the official template
 */
const CertificateTemplate = ({ data, phoenicianLogoSrc, stampImageSrc, certificationLogosSrc }) => {
  if (!data) return null;

  // Styles
  const styles = {
    goldColor: '#A2732E',
    blueColor: '#0066CC',
    redColor: '#FF0000',
    darkText: '#000000',
    grayText: '#666666',
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100%',
      fontSize: '12px',
      lineHeight: '1.4'
    }}>
      {/* ==================== HEADER ==================== */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '20px',
        paddingBottom: '10px'
      }}>
        {/* Left - Company Info */}
        <div style={{ flex: 1 }}>
          <h1 style={{ 
            fontSize: '18px', 
            fontWeight: 'bold', 
            color: styles.darkText,
            margin: '0 0 4px 0'
          }}>
            PHOENICIAN TECHNICAL SERVICES LLC
          </h1>
          <p style={{ fontSize: '11px', margin: '2px 0', color: styles.grayText }}>
            {data.companyAddress}
          </p>
          <p style={{ fontSize: '11px', margin: '2px 0', color: styles.grayText }}>
            {data.companyPhone}
          </p>
          <p style={{ fontSize: '11px', margin: '2px 0', color: styles.grayText, fontWeight: 'bold' }}>
            {data.companyTrn}
          </p>
        </div>
        
        {/* Right - Logo */}
        <div style={{ textAlign: 'right' }}>
          <img 
            src={phoenicianLogoSrc} 
            alt="Phoenician Logo" 
            style={{ height: '60px', objectFit: 'contain' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          
        </div>
      </div>

      {/* ==================== TITLE ==================== */}
      <div style={{ textAlign: 'center', margin: '30px 0 40px 0' }}>
        <h2 style={{ 
          fontSize: '20px', 
          fontWeight: 'bold', 
          color: styles.darkText,
          margin: 0,
          
        }}>
          COMPLETION CERTIFICATE
        </h2>
      </div>

      {/* ==================== TO SECTION ==================== */}
      <div style={{ marginBottom: '30px' }}>
        <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>To :</p>
        <p style={{ fontWeight: 'bold', margin: '4px 0' }}>{data.payee}</p>
        <p style={{ margin: '4px 0' }}>{data.poBox}</p>
        <p style={{ margin: '4px 0' }}>{data.trn}</p>
      </div>

      {/* ==================== INTRO TEXT ==================== */}
      <div style={{ marginBottom: '30px',fontSize:'13px' }}>
        <p style={{ lineHeight: '1.6' }}>
          We would like to inform you that the job awarded to us has been completed according to contract terms and schedule.
        </p>
      </div>

      {/* ==================== SERVICE DETAILS ==================== */}
      <div style={{ marginBottom: '30px' }}>
        <p style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '13px' }}>SERVICE DETAILS</p>
        
        <div style={{ display: 'flex', margin: '8px 0' }}>
          <div style={{ width: '120px', fontWeight: 'bold', verticalAlign: 'top' }}>Details</div>
          <div style={{ verticalAlign: 'top' }}>: {data.details}</div>
        </div>
        <div style={{ display: 'flex', margin: '8px 0' }}>
          <div style={{ width: '120px', fontWeight: 'bold', verticalAlign: 'top' }}>PO No.</div>
          <div style={{ verticalAlign: 'top' }}>: {data.agreementRef}</div>
        </div>
        <div style={{ display: 'flex', margin: '8px 0' }}>
          <div style={{ width: '120px', fontWeight: 'bold', verticalAlign: 'top' }}>Project Name</div>
          <div style={{ verticalAlign: 'top' }}>: {data.cluster}</div>
        </div>
        <div style={{ display: 'flex', margin: '8px 0' }}>
          <div style={{ width: '120px', fontWeight: 'bold', verticalAlign: 'top' }}>Service Month</div>
          <div style={{ verticalAlign: 'top', color: styles.redColor }}>: {data.monthDisplay}</div>
        </div>
        <div style={{ display: 'flex', margin: '8px 0' }}>
          <div style={{ width: '120px', fontWeight: 'bold', verticalAlign: 'top' }}>Service Type</div>
          <div style={{ verticalAlign: 'top' }}>: {data.serviceTypeDisplay}</div>
        </div>
      </div>

      {/* ==================== CERTIFICATION TEXT ==================== */}
      <div style={{ marginBottom: '30px' }}>
        <p style={{ lineHeight: '1.6', fontWeight: 'bold', fontStyle: 'italic' }}>
          We certify that the job has been completed as per schedule and based on the scope of work mentioned therein.
        </p>
      </div>

      {/* ==================== SIGNATURE SECTION ==================== */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '40px'
      }}>
        {/* Left - Certified By */}
        <div style={{ flex: 1 }}>
          <p style={{ marginBottom: '16px' }}>Certified by:</p>
          <p style={{ marginBottom: '20px' }}>{data.payee}</p>
          
          <div style={{ marginBottom: '16px' }}>
            <span style={{ marginRight: '10px' }}>Name:</span>
            <span style={{ 
              display: 'inline-block',
              width: '200px',
              borderBottom: '1px solid #000',
              verticalAlign: 'bottom',
              marginBottom: '-2px'
            }}></span>
          </div>
          
          <div>
            <span style={{ marginRight: '16px' }}>Date:</span>
            <span style={{ 
              display: 'inline-block',
              width: '200px',
              borderBottom: '1px solid #000',
              verticalAlign: 'bottom',
              marginBottom: '-2px'
            }}></span>
          </div>
        </div>
        
        {/* Right - Sign Here Box and Stamp Below */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-end'
        }}>
          {/* Sign Here Box */}
          <div style={{ 
            width: '220px',
            height: '100px',
            border: '2px solid #ccc',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fafafa',
            marginBottom: '20px'
          }}>
            <span style={{ 
              color: '#999', 
              fontSize: '14px',
              fontWeight: 'bold'
            }}>
              SIGN HERE
            </span>
          </div>

          {/* For Phoenician Section Below */}
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            {/* For Phoenician Text First */}
            <p style={{ fontWeight: 'bold', margin: '0 0 12px 0', textAlign: 'center' }}>
              For Phoenician Technical Services LLC
            </p>
            
            {/* Stamp Image Below the text */}
            <div style={{ 
              width: '160px', 
              height: '160px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img 
                src={stampImageSrc} 
                alt="Company Stamp" 
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '100%', 
                  objectFit: 'contain'
                }}
                onError={(e) => { 
                  // Fallback: show placeholder text if stamp image not available
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div style="width:140px;height:140px;border:2px solid #A2732E;border-radius:50%;display:flex;align-items:center;justify-content:center;text-align:center;font-size:11px;color:#A2732E;font-weight:bold;">PHOENICIAN<br/>DUBAI - UAE</div>';
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ==================== SPACER ==================== */}
      <div style={{ flex: 1, minHeight: '40px' }}></div>

      {/* ==================== FOOTER ==================== */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-end',
        paddingTop: '20px',
        borderTop: '1px solid #eee',
        marginTop: 'auto'
      }}>
        {/* Left - Company Address */}
        <div style={{ 
          fontSize: '9px',
          color: styles.goldColor, 
          lineHeight: '1.6',
          fontWeight: 'bold'
        }}>
          <p style={{ margin: '2px 0' }}>PHOENICIAN TECHNICAL SERVICES LLC</p>
          <p style={{ margin: '2px 0' }}>OFFICE 399 CENTRAL PLAZA, DIP-1, DUBAI, UAE</p>
          <p style={{ margin: '2px 0' }}>P.O.BOX: 377872 - DUBAI</p>
          <p style={{ margin: '2px 0' }}>T: 048829484</p>
          <p style={{ margin: '2px 0' }}>W: WWW.PHOENICIAN-UAE.COM</p>
        </div>
        
        {/* Right - Certification Logos */}
        <div>
          <img 
            src={certificationLogosSrc} 
            alt="Certification Logos" 
            style={{ height: '50px', objectFit: 'contain' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      </div>
    </div>
  );
};

export default CompletionCertificate;